// Admin › Help › one article: the editor. Fields and body on the left, the
// source rows and revisions in the middle, the checks on the right. The checks
// run as you type, with the same package the API uses (@vortex/help-render);
// the API runs them again before a revision can be ready for check. Every save
// is a new draft revision on top of the newest one. Takes the whole window.
// docs/wip/SCOUT_HELP.md

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { runChecks, type MediaInfo } from '@vortex/help-render';
import { api } from '../../../services/api';
import { BlocksEditor } from './BlocksEditor';
import { HelpPreview } from './HelpPreview';
import { HelpFlags } from './HelpFlags';
import {
  APPS,
  STATE_LABELS,
  TYPE_LABELS,
  errorText,
  useHelpArticle,
  useHelpArticles,
  useHelpMedia,
  useHelpSections,
  useMarkReady,
  useSaveRevision,
  type CheckResult,
  type HelpBlock,
  type HelpMedia,
  type HelpRevision,
  type NeedsSoftware,
  type Problem,
  type RevisionBody,
  type SafetyKind,
} from './data/help';

const input = 'min-h-[44px] w-full rounded-[10px] border border-slate-400/20 bg-slate-900/60 px-3 text-[15px] text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/40';
const label = 'flex flex-col gap-1.5 text-sm font-medium text-slate-300';
const button = 'inline-flex min-h-[44px] items-center rounded-[10px] px-4 text-[15px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:opacity-40';

type Draft = Omit<RevisionBody, 'basedOn'>;

const draftOf = (r: HelpRevision): Draft => ({
  title: r.title, summary: r.summary, whereToFind: r.whereToFind, requirements: r.requirements, body: r.body, safety: r.safety,
  keywords: r.keywords, needsSoftware: r.needsSoftware, plans: r.plans, related: r.related, seenLabels: r.seenLabels, rows: r.rows.map((x) => x.docId),
});

const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
const blank = (s: string | null | undefined) => (s && s.trim() ? s.trim() : null);

/** What the API would refuse before any check runs: empty fields and unchosen files. */
export function gaps(d: Draft): string[] {
  const out: string[] = [];
  if (!d.title.trim()) out.push('The title is empty');
  if (!d.summary.trim()) out.push('The summary is empty');
  d.body.forEach((b, i) => {
    const where = `Block ${i + 1}`;
    if ((b.type === 'paragraph' || b.type === 'subheading' || b.type === 'note') && !b.text.trim()) out.push(`${where}: the text is empty`);
    if (b.type === 'steps') b.items.forEach((s, n) => { if (!s.text.trim()) out.push(`${where}: step ${n + 1} is empty`); });
    if ((b.type === 'image' || b.type === 'clip') && !b.mediaId) out.push(`${where}: choose a file`);
    if (b.type === 'link' && !b.topicId) out.push(`${where}: choose an article`);
    if (b.type === 'table' && b.head.some((h) => !h.trim())) out.push(`${where}: every column needs a heading`);
  });
  d.needsSoftware.forEach((s, i) => { if (!/^\d+\.\d+\.\d+$/.test(s.version)) out.push(`Needs software ${i + 1}: a version looks like 3.93.0`); });
  return out;
}

const mediaInfo = (m: HelpMedia): MediaInfo => ({
  id: m.id, kind: m.kind, alt: m.alt, noPersonalData: m.noPersonalData, bytes: m.bytes, width: m.width, height: m.height, durationS: m.durationS,
  src: api.adminHelpMediaUrl(m.id), stillSrc: m.posterKey ? api.adminHelpMediaUrl(m.id, true) : null, captionsSrc: null,
});

/** Swaps a bold label's text everywhere in the body: "Use CONFIRM ANCHOR". */
function swapLabel(body: HelpBlock[], from: string, to: string): HelpBlock[] {
  const swap = (t: string) => t.split(`**${from}**`).join(`**${to}**`);
  return body.map((b) => {
    switch (b.type) {
      case 'paragraph': case 'subheading': case 'note': return { ...b, text: swap(b.text) };
      case 'steps': return { ...b, items: b.items.map((s) => ({ ...s, text: swap(s.text) })) };
      case 'table': return { ...b, head: b.head.map(swap), rows: b.rows.map((r) => r.map(swap)) };
      case 'image': case 'clip': return b.caption ? { ...b, caption: swap(b.caption) } : b;
      case 'link': return b.text ? { ...b, text: swap(b.text) } : b;
      default: return b;
    }
  });
}

export function HelpEditorPage() {
  const { id } = useParams();
  const detail = useHelpArticle(id);
  const articles = useHelpArticles();
  const sections = useHelpSections();
  const media = useHelpMedia();
  const save = useSaveRevision(id);
  const ready = useMarkReady(id);

  const revision = detail.data?.revision ?? null;
  const [draft, setDraft] = useState<Draft | null>(null);
  // A fresh copy of the newest revision after each load or save.
  useEffect(() => { if (revision) setDraft(draftOf(revision)); }, [revision]);

  const saved = revision ? draftOf(revision) : null;
  const dirty = !!draft && !!saved && JSON.stringify(draft) !== JSON.stringify(saved);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const article = detail.data?.article;
  const mediaList = useMemo(() => (media.data ?? []).filter((m) => m.kind !== 'video'), [media.data]);
  const others = useMemo(() => (articles.data ?? []).filter((a) => !a.retiredAt && a.id !== id).map((a) => ({ topicId: a.topicId, title: a.latest.title })), [articles.data, id]);
  const live = useMemo(() => new Set((articles.data ?? []).filter((a) => !a.retiredAt).map((a) => a.topicId)), [articles.data]);

  const checks: CheckResult[] = useMemo(() => {
    if (!draft || !article) return [];
    const rowText = new Map((revision?.rows ?? []).map((r) => [r.docId, r.rowText]));
    return runChecks({ ...draft, topicId: article.topicId, type: article.type }, {
      rows: draft.rows.map((docId) => ({ docId, rowText: rowText.get(docId) ?? {} })),
      media: Object.fromEntries((media.data ?? []).map((m) => [m.id, mediaInfo(m)])),
      liveTopics: live,
    });
  }, [draft, article, revision, media.data, live]);
  const failing = checks.filter((c) => !c.ok).length;
  const previewMedia = useMemo(() => Object.fromEntries((media.data ?? []).map((m) => [m.id, mediaInfo(m)])), [media.data]);
  // Links inside the preview go nowhere: the frame shows the article, not the site.
  const previewArticles = useMemo(() => Object.fromEntries((articles.data ?? []).map((a) => [a.topicId, { href: `#${a.topicId}`, title: a.latest.title }])), [articles.data]);
  const previewContent = useMemo(() => (draft && article ? { ...draft, topicId: article.topicId, type: article.type } : null), [draft, article]);
  const missing = draft ? gaps(draft) : [];

  if (detail.isLoading) return <Shell><p className="text-[15px] text-slate-400">Loading…</p></Shell>;
  if (detail.isError || !article) return <Shell><p className="text-[15px] text-red-400">Couldn't load the article.</p></Shell>;
  if (!revision || !draft) return <Shell><p className="text-[15px] text-slate-400">This article has no revision yet.</p></Shell>;

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft({ ...draft, [k]: v });
  const section = sections.data?.find((s) => s.id === article.sectionId);
  const doSave = () => save.mutate({ basedOn: revision.id, ...draft, whereToFind: blank(draft.whereToFind), requirements: blank(draft.requirements) });
  const readyError = ready.error as { response?: { data?: { details?: CheckResult[] } } } | null;

  const fix = (p: Problem) => (
    <span className="mt-1 flex flex-wrap gap-x-3">
      {p.use && <button type="button" className="text-[15px] font-medium text-cyan-300 hover:text-cyan-200" onClick={() => set('body', swapLabel(draft.body, p.label!, p.use!))}>Use {p.use}</button>}
      {p.label && <button type="button" className="text-[15px] font-medium text-cyan-300 hover:text-cyan-200" onClick={() => set('seenLabels', [...draft.seenLabels, p.label!])}>Seen on the Scout</button>}
    </span>
  );

  return (
    <div className="admin-console min-h-screen bg-slate-900 text-slate-300">
      <header className="z-10 flex sm:sticky sm:top-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-400/15 bg-slate-900/95 px-4 py-3 backdrop-blur sm:px-6">
        <Link to="/admin/help/articles" className="inline-flex min-h-[44px] items-center gap-1.5 text-[15px] font-medium text-slate-400 hover:text-white">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />Articles
        </Link>
        <span className="font-mono text-[15px] text-slate-400">{article.topicId}</span>
        <h1 className="min-w-0 text-[19px] font-semibold text-white">{draft.title || 'Untitled'}</h1>
        <span className="rounded-full bg-slate-400/15 px-2.5 py-0.5 text-[13px] font-semibold text-slate-200">{dirty ? 'Unsaved changes' : STATE_LABELS[revision.state]}</span>
        <span className="text-[15px] text-slate-400">Revision {revision.number} by {revision.authorName}</span>
        <span className="flex-1" />
        <button type="button" onClick={doSave} disabled={!dirty || missing.length > 0 || save.isPending} className={`${button} border border-slate-400/25 text-white hover:bg-slate-400/10`}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => ready.mutate(revision.id)}
          disabled={dirty || revision.state !== 'draft' || failing > 0 || ready.isPending}
          className={`${button} bg-cyan-600 text-white hover:bg-cyan-500`}
        >
          Ready for check
        </button>
        {revision.state === 'ready' && !dirty && (
          <Link to={`/admin/help/articles/${article.id}/check`} className={`${button} bg-cyan-600 text-white hover:bg-cyan-500`}>Device check</Link>
        )}
        {failing > 0 && <span className="text-[15px] text-amber-300">{failing} check{failing === 1 ? '' : 's'} to fix</span>}
      </header>

      <div className="grid gap-6 px-4 pb-16 pt-6 sm:px-6 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)_minmax(0,320px)]">
        <div className="space-y-5">
          {save.isError && <p role="alert" className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-[15px] text-red-200">{errorText(save.error)}</p>}
          {missing.length > 0 && dirty && (
            <div role="status" className="rounded-xl border border-amber-400/30 bg-amber-400/[0.06] px-4 py-3 text-[15px] text-amber-200">
              Before it saves: {missing.join('; ')}.
            </div>
          )}
          <label className={label}>Title<input className={input} value={draft.title} onChange={(e) => set('title', e.target.value)} /></label>
          <label className={label}>
            <span className="flex justify-between">Summary <span className={`font-normal ${draft.summary.length > 160 ? 'text-amber-300' : 'text-slate-500'}`}>{draft.summary.length} / 160</span></span>
            <textarea className={`${input} min-h-[72px] py-2.5`} value={draft.summary} onChange={(e) => set('summary', e.target.value)} />
          </label>
          <p className="text-[15px] text-slate-400">{TYPE_LABELS[article.type]} · {section?.title ?? 'Section'} · /{section?.slug ?? ''}/{article.slug}</p>
          <label className={label}>Where to find it<input className={input} value={draft.whereToFind ?? ''} placeholder="Anchor, on the Home screen" onChange={(e) => set('whereToFind', e.target.value)} /></label>
          <label className={label}>Needs<input className={input} value={draft.requirements ?? ''} placeholder="A GPS position" onChange={(e) => set('requirements', e.target.value)} /></label>
          <SoftwareField value={draft.needsSoftware} onChange={(v) => set('needsSoftware', v)} />
          <label className={label}>
            Safety line
            <select className={input} value={draft.safety ?? ''} onChange={(e) => set('safety', (e.target.value || null) as SafetyKind | null)}>
              <option value="">None</option>
              <option value="anchor">Anchor</option>
              <option value="mob">Man overboard</option>
              <option value="routes">Routes</option>
            </select>
          </label>
          <label className={label}>Keywords (search only, commas between)<input className={input} value={draft.keywords.join(', ')} onChange={(e) => set('keywords', list(e.target.value))} /></label>
          <label className={label}>Plans (commas between)<input className={input} value={draft.plans.join(', ')} onChange={(e) => set('plans', list(e.target.value))} /></label>
          <RelatedField value={draft.related} onChange={(v) => set('related', v)} options={others} />
          <div className="space-y-2">
            <h2 className="text-sm font-medium text-slate-300">Body</h2>
            <BlocksEditor blocks={draft.body} onChange={(b) => set('body', b)} media={mediaList} articles={others} />
          </div>
        </div>

        <div className="min-w-0 space-y-6">
          {previewContent && <HelpPreview article={previewContent} media={previewMedia} articles={previewArticles} />}
          <SourceRows rows={draft.rows} saved={revision.rows} onChange={(r) => set('rows', r)} />
          <section className="space-y-2">
            <h2 className="text-[17px] font-semibold text-white">Revisions</h2>
            <ul className="overflow-hidden rounded-2xl border border-slate-400/15 bg-slate-800/45">
              {detail.data!.revisions.map((r) => (
                <li key={r.id} className="flex flex-wrap gap-x-3 border-t border-slate-400/10 px-4 py-2.5 text-[15px] first:border-t-0">
                  <span className="text-slate-200">Revision {r.number}</span>
                  <span className="text-slate-400">{STATE_LABELS[r.state]} · {r.claudeDraft ? 'Claude draft' : r.authorName}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <div className="space-y-6">
          <HelpFlags flags={detail.data!.flags ?? []} articleId={article.id} />
          <aside aria-label="Checks" className="space-y-3">
            <h2 className="text-[17px] font-semibold text-white">Checks</h2>
            {readyError?.response?.data?.details && <p className="text-[15px] text-amber-300">The API found checks to fix; they are listed below.</p>}
            {ready.isError && !readyError?.response?.data?.details && <p className="text-[15px] text-red-400">{errorText(ready.error)}</p>}
            <ul className="overflow-hidden rounded-2xl border border-slate-400/15 bg-slate-800/45">
              {checks.map((c) => (
                <li key={c.id} className="border-t border-slate-400/10 px-4 py-3 first:border-t-0">
                  {c.ok ? (
                    <span className="flex gap-2 text-[15px] text-slate-200"><CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" aria-hidden="true" />{c.message}</span>
                  ) : (
                    <div className="space-y-2">
                      <span className="flex gap-2 text-[15px] font-medium text-amber-200"><ExclamationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" aria-hidden="true" />{c.message}</span>
                      <ul className="space-y-2 pl-7">
                        {c.problems.map((p, i) => <li key={i} className="text-[15px] text-amber-100">{p.text}{(p.use || p.label) && fix(p)}</li>)}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-[15px] text-slate-400">The same checks run on a Claude draft and a hand edit, and again before a revision is ready for check.</p>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="admin-console min-h-screen bg-slate-900 px-6 py-8 text-slate-300">
      <Link to="/admin/help/articles" className="mb-6 inline-flex min-h-[44px] items-center gap-1.5 text-[15px] font-medium text-slate-400 hover:text-white">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />Articles
      </Link>
      {children}
    </div>
  );
}

function SoftwareField({ value, onChange }: { value: NeedsSoftware[]; onChange: (v: NeedsSoftware[]) => void }) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-slate-300">Needs software</legend>
      {value.map((s, i) => (
        <div key={i} className="flex gap-2">
          <select aria-label={`App ${i + 1}`} className={input} value={s.app} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, app: e.target.value } : x)))}>
            {APPS.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
          </select>
          <input aria-label={`Version ${i + 1}`} className={`${input} w-32 font-mono`} value={s.version} placeholder="1.14.0" onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, version: e.target.value.trim() } : x)))} />
          <button type="button" className="min-h-[44px] rounded-[10px] px-2 text-[15px] text-slate-400 hover:text-white" onClick={() => onChange(value.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      {value.length < 6 && (
        <button type="button" className="text-[15px] font-medium text-cyan-300 hover:text-cyan-200" onClick={() => onChange([...value, { app: 'vn', version: '' }])}>
          Add an app version
        </button>
      )}
    </fieldset>
  );
}

function RelatedField({ value, onChange, options }: { value: string[]; onChange: (v: string[]) => void; options: { topicId: string; title: string }[] }) {
  const left = options.filter((o) => !value.includes(o.topicId));
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-slate-300">Related (up to four)</legend>
      <ul className="flex flex-wrap gap-2">
        {value.map((t) => (
          <li key={t} className="inline-flex items-center gap-2 rounded-full bg-slate-400/15 py-1 pl-3 pr-1 text-[15px] text-slate-200">
            {t}
            <button type="button" aria-label={`Remove ${t}`} className="rounded-full px-2 text-slate-400 hover:text-white" onClick={() => onChange(value.filter((x) => x !== t))}>×</button>
          </li>
        ))}
      </ul>
      {value.length < 4 && left.length > 0 && (
        <select aria-label="Add a related article" className={input} value="" onChange={(e) => e.target.value && onChange([...value, e.target.value])}>
          <option value="">Add a related article…</option>
          {left.map((o) => <option key={o.topicId} value={o.topicId}>{o.topicId} · {o.title}</option>)}
        </select>
      )}
    </fieldset>
  );
}

const ROW_COLUMNS = ['Element', 'How it is used', 'What it does'];

function SourceRows({ rows, saved, onChange }: { rows: string[]; saved: HelpRevision['rows']; onChange: (r: string[]) => void }) {
  const [adding, setAdding] = useState('');
  const textOf = new Map(saved.map((r) => [r.docId, r.rowText]));
  const add = () => {
    const d = adding.trim().toUpperCase();
    if (d && !rows.includes(d)) onChange([...rows, d]);
    setAdding('');
  };
  return (
    <section className="space-y-2">
      <h2 className="text-[17px] font-semibold text-white">Source rows</h2>
      <p className="text-[15px] text-slate-400">The register rows this article is written from. Bold labels are checked against their text.</p>
      <ul className="overflow-hidden rounded-2xl border border-slate-400/15 bg-slate-800/45">
        {rows.length === 0 && <li className="px-4 py-3 text-[15px] text-slate-400">None yet.</li>}
        {rows.map((d) => {
          const t = textOf.get(d) ?? {};
          const parts = ROW_COLUMNS.map((c) => t[c]).filter(Boolean);
          return (
            <li key={d} className="flex gap-3 border-t border-slate-400/10 px-4 py-3 first:border-t-0">
              <span className="w-40 shrink-0 font-mono text-[15px] text-slate-400">{d}</span>
              <span className="min-w-0 flex-1 text-[15px] text-slate-200">
                {parts.length ? parts.join(' · ') : textOf.has(d) ? 'No text from the register yet.' : 'Text appears once saved and the register updates job sends rows.'}
              </span>
              <button type="button" aria-label={`Remove ${d}`} className="shrink-0 text-[15px] text-slate-400 hover:text-white" onClick={() => onChange(rows.filter((x) => x !== d))}>Remove</button>
            </li>
          );
        })}
      </ul>
      <div className="flex gap-2">
        <input
          aria-label="Add a source row by Doc ID"
          className={`${input} font-mono`}
          value={adding}
          placeholder="ANCHOR-007"
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button type="button" className={`${button} border border-slate-400/25 text-white hover:bg-slate-400/10`} onClick={add}>Add</button>
      </div>
    </section>
  );
}
